import { commonMessages } from '@common/intl'
import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

interface WindowTitleConsumerProps {
  setTitle: (title: string) => string
  removeTitle: (key: string) => void
  /** Numeric prefix shown as "(3) …" — e.g. unread messages. 0 hides it. */
  setTitleBadge: (count: number) => void
}

const context = createContext<WindowTitleConsumerProps | undefined>(undefined)

export function useWindowTitleContext() {
  return useContext(context)
}

type KeyedTitle = {
  key: string
  title: string
}
export function WindowTitleProvider({ children }: PropsWithChildren) {
  const intl = useIntl()
  const titleRef = useRef<HTMLTitleElement | null>(null)

  // Remove the original DOM title
  useEffect(() => {
    Array.from(document.getElementsByTagName('title')).forEach((el) => {
      if (el === titleRef.current) {
        return
      }

      el.remove()
    })
  }, [])

  const [titleStack, setTitleStack] = useState<KeyedTitle[]>([])

  const setTitle = useCallback((title: string) => {
    const key = Math.random().toString(36).substring(2, 16)
    setTitleStack((prevState) => [
      ...prevState,
      {
        key,
        title,
      },
    ])
    return key
  }, [])

  const removeTitle = useCallback((key: string) => {
    setTitleStack((prevState) => prevState.filter((i) => i.key !== key))
  }, [])

  const [titleBadge, setTitleBadge] = useState(0)

  const title = titleStack.length > 0 ? titleStack[titleStack.length - 1] : null

  const badgePrefix = titleBadge > 0 ? `(${titleBadge > 99 ? '99+' : titleBadge}) ` : ''
  const titleText =
    badgePrefix +
    [title?.title, intl.formatMessage(commonMessages.webTitle)].filter(Boolean).join(' | ')

  const providerValue = useMemo(
    (): WindowTitleConsumerProps => ({ removeTitle, setTitle, setTitleBadge }),
    [setTitle, removeTitle],
  )

  return (
    <context.Provider value={providerValue}>
      <title ref={titleRef}>{titleText}</title>
      {children}
    </context.Provider>
  )
}
